import React, { useMemo, useState } from "react";
import { X, Trash2, MessageCircle, Bookmark } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { T, O, tr } from "../lib/i18n";
import { getRoomSession } from "../lib/roomSession";

const inputCls = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#4a1c17] focus:ring-2 focus:ring-[#4a1c17]/15";

/* mode "notes": personal list (no ordering). mode "order": WhatsApp order for pool/spa/room menus. */
export function SelectionSheet({ open, onOpenChange, items, onRemove, onClear, lang, mode = "notes", order }) {
  const total = items.reduce((sum, it) => sum + it.price.value * it.qty, 0);
  const isOrder = mode === "order";
  const session = useMemo(() => getRoomSession(), [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const params = new URLSearchParams(window.location.search);
  const qrPlace = params.get("posto") || params.get("place") || "";
  const roomName = session?.name || (session ? `${session.slug.charAt(0).toUpperCase()}${session.slug.slice(1)}` : "");
  const presetPlace = qrPlace || (session ? `Camera ${roomName}` : "");
  // Always the generic label: guests may order from a sunbed, the spa or their room
  const placeLabel = tr(O.where, lang);
  const [place, setPlace] = useState(presetPlace);
  const [name, setName] = useState(session?.guest || "");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");

  const sendWhatsApp = () => {
    if (!place.trim()) { setErr(tr(O.whereReq, lang)); return; }
    setErr("");
    // Message always in Italian: the staff reads it.
    const lines = items.map((it) => `• ${it.qty}× ${tr(it.dish.name, "it")}${it.price.label ? ` (${it.price.label})` : ""}`);
    const msg = [
      `Ordine dal ${order?.menuName || "menu"}`,
      `Dove: ${place.trim()}`,
      name.trim() ? `Nome: ${name.trim()}` : null,
      "",
      ...lines,
      "",
      `Totale stimato: ${total.toFixed(2)} €`,
      notes.trim() ? `Note: ${notes.trim()}` : null,
    ].filter((l) => l !== null).join("\n");
    window.open(`https://wa.me/${order?.phone}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl p-0 flex flex-col" data-testid="selection-sheet">
        <div className="px-5 pt-5 pb-3 border-b border-neutral-100">
          <div className="flex items-start justify-between gap-4">
            <div>
              <SheetTitle className="font-serif text-xl font-bold text-neutral-900 inline-flex items-center gap-2">
                {isOrder ? <MessageCircle className="h-5 w-5 text-emerald-600" /> : <Bookmark className="h-5 w-5 text-[#C48B31]" />}
                {isOrder ? tr(O.sendOrder, lang) : tr(O.notes, lang)}
              </SheetTitle>
              <p className="mt-1 text-xs text-neutral-500 max-w-xs" data-testid="selection-subtitle">{isOrder ? tr(O.orderSub, lang) : tr(O.notesSub, lang)}</p>
            </div>
            <button onClick={() => onOpenChange(false)} className="h-9 w-9 rounded-full hover:bg-neutral-100 flex items-center justify-center" data-testid="selection-close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="h-full flex items-center justify-center text-center px-6">
              <p className="text-sm text-neutral-500 max-w-xs">{tr(T.selectionEmpty, lang)}</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map((it, i) => (
                <li key={`${it.dish.id}-${it.price.label ?? "std"}-${i}`} className="flex items-center gap-3 py-2 border-b border-neutral-50 last:border-b-0">
                  {it.dish.image ? (
                    <img src={it.dish.image} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" />
                  ) : (
                    <div className="h-12 w-12 rounded-lg bg-neutral-100 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-neutral-900 truncate">{tr(it.dish.name, lang)}</div>
                    {it.price.label && <div className="text-xs text-neutral-500">{it.price.label}</div>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-semibold text-neutral-900">
                      {it.qty > 1 && <span className="text-neutral-500 mr-1">{it.qty}×</span>}
                      {(it.price.value * it.qty).toFixed(2)} €
                    </span>
                    <button onClick={() => onRemove(i)} className="h-8 w-8 rounded-full hover:bg-rose-50 text-neutral-400 hover:text-rose-600 flex items-center justify-center" data-testid={`selection-remove-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {isOrder && items.length > 0 && (
            <div className="mt-5 space-y-2.5" data-testid="order-form">
              <label className="block text-xs font-semibold text-neutral-700">{placeLabel}
                <input value={place} onChange={(e) => setPlace(e.target.value)} placeholder={tr(O.wherePh, lang)} className={`${inputCls} mt-1`} data-testid="order-place-input" />
              </label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder={tr(O.yourName, lang)} className={inputCls} data-testid="order-name-input" />
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={tr(O.orderNotes, lang)} rows={2} className={inputCls} data-testid="order-notes-input" />
              {err && <p className="text-xs text-rose-600" data-testid="order-error">{err}</p>}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-neutral-100 px-5 py-4 bg-neutral-50">
            <div className="flex items-center justify-between">
              <span className="text-sm text-neutral-500">{isOrder ? tr(T.total, lang) : tr(O.estimate, lang)}</span>
              <span className="text-xl font-bold text-neutral-900">{total.toFixed(2)} €</span>
            </div>
            {isOrder && <p className="mt-1 text-[11px] text-neutral-400 leading-snug" data-testid="order-service-note">{tr(O.serviceNote, lang)}</p>}
            {isOrder && (
              <button onClick={sendWhatsApp} className="mt-3 w-full flex items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 py-3 text-sm font-bold text-white shadow-md" data-testid="order-send-wa">
                <MessageCircle className="h-4 w-4" /> {tr(O.sendWa, lang)}
              </button>
            )}
            <button onClick={onClear} className="mt-2 w-full flex items-center justify-center gap-2 rounded-full border border-neutral-300 bg-white py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-100" data-testid="selection-clear">
              <Trash2 className="h-4 w-4" /> {tr(T.clear, lang)}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
